<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { routes } from '../../router';
import Loading from '../Loading';
import { getUser } from '../../utils/api';
import isMobile from '../../utils/isMobile';
import {
  isExpanded,
  expand,
  collapse,
  expandCollapse,
} from '../../utils/expandCollapse';
import {
  TRIAL_DURATION,
  TRIAL_ONGOING,
  TRIAL_ENDED,
  SUBSCRIPTION_ONGOING,
  SUBSCRIPTION_CANCELLED,
} from '../../utils/constants';
import { default as t } from '../../utils/translationStrings';

// Data.
const router = useRouter();
const loading = ref(true);
const pageData = ref({});
const user = ref({});
const banner = ref({});
const onMobile = ref(isMobile());

// Template ref.
const message = ref(null);

// Props.
const props = defineProps({
  shopUrl: {
    type: String,
    required: true,
  },
});

// Computed.
const activePage = computed(() => {
  return router.currentRoute.value;
});

const bannerIsOpen = computed(() => {
  return isExpanded(message.value);
});

const daysLeft = computed(() => {
  // Early return if isTrial is false.
  

  return 0;
});

// Based on some values, determine at which state the subscription is.
const subscriptionStatus = computed(() => {
  if (pageData.value?.specs?.isTrial) {
    return daysLeft.value === 0 ? TRIAL_ENDED : TRIAL_ONGOING;
  }

  if (pageData.value?.specs?.cancelledAt?.date) {
    return SUBSCRIPTION_CANCELLED;
  }

  // If next payment is set, assume subscription is ongoing.
  if (pageData.value?.specs?.nextPossibleCancelledDate?.date) {
    return SUBSCRIPTION_ONGOING;
  }

  // Fallback?
  return TRIAL_ENDED;
});

// Methods.
const fetchData = async () => {
  try {
    // Get information from Shopware.
    const response = await getUser();
    pageData.value = response.data;
    user.value = pageData.value.context?.customer;
  } catch (error) {
    console.log('Task failed successfully.', error);
  } finally {
    // We check for customer data because user gets logged out automatically when idle.
    if (user.value?.id) {
      // Remove loading animation.
      loading.value = false;
      // Set the banner text & button URL based on subscription status.
      buildBanner();
      // Navigate to route if URL has #.
      preselectActivePage();
    } else {
      banner.value.text = t.dashboard.sessionExpired;
    }
  }
};

const preselectActivePage = () => {
  const { hash } = window.location;
  if (hash) {
    const preselectedPage = routes.find((route) => {
      return route.name === `${hash}`.replace('#', '');
    });

    // Default page: user information (first tab).
    router.push(preselectedPage ? preselectedPage.path : '/');
  }
};

const buildBanner = () => {
  // Return text based on subscription status.
  if (activePage.value?.name === 'dashboard') {
    banner.value.linkTitle = t.dashboard.completeSubscription;
    banner.value.linkUrl = `${props.shopUrl}/checkout/real-product`;

    banner.value.text =
      daysLeft.value === 0
        ? t.dashboard.trialEndedMessage.replace(
          '@trial_duration',
          TRIAL_DURATION,
        )
        : t.dashboard.untilTrialEnd.replace('@days_left', daysLeft.value);
  }
};

const toggleBannerHeight = () => {
  // Should only be triggered on mobile.
  if (onMobile.value && message.value) {
    expandCollapse(message.value);
  }
};

// Mounted.
onMounted(() => {
  // Get the user data.
  fetchData();

  window.addEventListener('resize', () => {
    onMobile.value = isMobile();

    if (onMobile.value && message.value) {
      // If resizing from XS to XL, unset properties bc message is
      // only expandable on mobile. No real user will ever be able to
      // resize their phone so this is only for Kassandra's requests.
      message.value.style.height = '';
      message.value.style.opacity = '';
    }
  });

  // Toggle message visibility on scroll.
  window.addEventListener('scroll', () => {
    if (onMobile.value && pageData.value?.specs?.isTrial) {
      window.scrollY < 10 ? expand(message.value) : collapse(message.value);
    }
  });
});
</script>

<template>
  <div class="dashboard">
    <!-- Status banner/message. -->
    <div v-if="!loading && pageData?.specs?.isTrial" class="dashboard__message" :class="[
      `dashboard__message--${bannerIsOpen ? 'expanded' : 'closed'}`,
      subscriptionStatus === TRIAL_ONGOING ? 'messages--trial' : 'messages--ended'
    ]" @click="toggleBannerHeight">
      <i class="fa-regular fa-info-circle" ref="icon"></i>
      <!-- Message content with variable height on mobile. -->
      <div ref="message">
        <span>{{ banner.text }}&nbsp;</span>
        <a :href="banner.linkUrl" target="_blank">
          {{ banner.linkTitle }}
        </a>
      </div>
    </div>

    <!-- Side navigation (desktop). -->
    <div v-if="!onMobile" class="dashboard__navigation dashboard__navigation--side">
      <nav class="dashboard__navigation-links">
        <RouterLink v-for="route in routes" :to="{ name: route.name }" class="dashboard__navigation-link" :class="{
          'dashboard__navigation-link--active':
            route.name === activePage.name,
        }" :key="route.name">
          <i :class="`fa-regular fa-${route.meta.icon}`"></i>
          <span v-html="route.meta.title"></span>
        </RouterLink>
      </nav>
    </div>

    <!-- Dashboard content. -->
    <div v-if="!loading" class="dashboard__pages">
      <div class="dashboard__content">
        <div class="dashboard-page">
          <div class="dashboard-page__header">
            <!-- Need to use v-html, bc of long German words. -->
            <h1 v-html="activePage?.name === 'dashboard'
              ? `${t.dashboardPage.welcomeBack}, ${user?.firstName} ${user?.lastName}!`
              : activePage?.meta.title
              "></h1>
            <span v-if="activePage?.meta.description" v-html="activePage.meta.description"></span>
          </div>
          <div class="dashboard-page__content">
            <!-- This is not usually how you use vue-router. -->
            <!-- The only reason for the slotted view is the Drupal markup. -->
            <RouterView v-slot="{ Component }">
              <Transition name="page-change">
                <Component :is="Component" :account-specs="pageData?.specs" :shop-url="shopUrl" :user="user"
                  :subscription-type="subscriptionStatus" :days-left="daysLeft" :trial-duration="TRIAL_DURATION"
                  @requestRefresh="fetchData">
                  <slot v-if="activePage.name === 'support-request'" name="webform"></slot>
                </Component>
              </Transition>
            </RouterView>
          </div>
          <div v-if="activePage.name !== 'support-request'" class="dashboard-page__footer">
            <h3>{{ t.dashboard.paymentQuestion }}</h3>
            <p>{{ t.dashboard.paymentInfo }}</p>
            <RouterLink class="button button--border" to="/support-request">
              {{ `${t.to} ${t.dashboardPage.formSupport}` }}
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
    <Loading v-else />

    <!-- Mobile dashboard navigation (fixed to bottom). -->
    <div v-if="onMobile" class="dashboard__navigation dashboard__navigation--bottom">
      <nav class="dashboard__navigation-links">
        <RouterLink v-for="route in routes" class="dashboard__navigation-link" :class="{
          'dashboard__navigation-link--active':
            route.name === activePage.name,
        }" :key="route.name" :to="{ name: route.name }">
          <i :class="`fa-regular fa-${route.meta.icon}`"></i>
          <span v-html="route.meta.title"></span>
        </RouterLink>
      </nav>
    </div>
  </div>
</template>
